import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDateTime, formatApiError, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Zap, CheckCircle2, X, Trash2, Sparkles, Info } from "lucide-react";

/** Intelligent Email Automation dashboard — a single page with 4 tabs. */
export default function EmailCapture() {
    const [tab, setTab] = useState("captures");
    const [captures, setCaptures] = useState([]);
    const [rules, setRules] = useState([]);
    const [logs, setLogs] = useState([]);
    const [cards, setCards] = useState([]);
    const [busy, setBusy] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    const load = useCallback(async () => {
        try {
            const [c, r, l, cs] = await Promise.all([
                api.get("/email-capture/captures"),
                api.get("/email-capture/rules"),
                api.get("/email-capture/logs?limit=30"),
                api.get("/cards"),
            ]);
            setCaptures(c.data.rows || []);
            setRules(r.data.rows || []);
            setLogs(l.data.rows || []);
            setCards(cs.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sync = async () => {
        setBusy(true);
        setSyncResult(null);
        try {
            const res = await api.post("/email-capture/sync", { days: 30 });
            setSyncResult(res.data);
            const { auto_posted, needs_review, subscriptions_detected } = res.data;
            if (auto_posted + needs_review > 0) {
                toast.success(`${auto_posted} auto-posted · ${needs_review} for review${subscriptions_detected > 0 ? ` · ${subscriptions_detected} subscription${subscriptions_detected > 1 ? "s" : ""} detected` : ""}`);
            } else {
                toast.message("Nothing new", { description: "No new transaction emails in the last 30 days." });
            }
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const auto = captures.filter((c) => c.status === "auto_posted");
    const review = captures.filter((c) => c.status === "needs_review");

    return (
        <div data-testid="email-capture-page">
            <div className="mb-6 flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow">Email Automation</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Your inbox is your accountant.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Every card-swipe alert email is read, parsed, categorized, deduped, and posted as an expense — or queued for review when the system isn&rsquo;t sure. Nothing sensitive is stored beyond the last 4 digits.
                    </p>
                </div>
                <Button onClick={sync} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="email-capture-sync-btn">
                    {busy ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Scanning inbox…</> : <><Zap size={13} className="mr-1.5" /> Scan last 30 days</>}
                </Button>
            </div>

            {syncResult && (
                <div className="text-[12px] text-stone-600 mb-6 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="email-capture-sync-summary">
                    <SummaryChip label="Scanned" value={syncResult.messages_scanned} />
                    <SummaryChip label="Auto-posted" value={syncResult.auto_posted} tone="emerald" />
                    <SummaryChip label="Needs review" value={syncResult.needs_review} tone="amber" />
                    <SummaryChip label="Duplicates" value={syncResult.duplicates_skipped} />
                    <SummaryChip label="Unparseable" value={syncResult.unparseable_skipped} />
                    <SummaryChip label="New subs" value={syncResult.subscriptions_detected} tone="blue" />
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mb-4 flex-wrap gap-1">
                    <TabsTrigger value="captures" data-testid="tab-auto">
                        Auto-Captured <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{auto.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="review" data-testid="tab-review">
                        Needs Review <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{review.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="rules" data-testid="tab-rules">
                        Merchant Rules <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{rules.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="logs" data-testid="tab-logs">
                        Parse Logs
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="captures">
                    <CapturesList rows={auto} cards={cards} onChange={load} readonly />
                </TabsContent>
                <TabsContent value="review">
                    <CapturesList rows={review} cards={cards} onChange={load} />
                </TabsContent>
                <TabsContent value="rules">
                    <RulesTab rules={rules} onChange={load} />
                </TabsContent>
                <TabsContent value="logs">
                    <LogsTab logs={logs} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

const TONE = {
    stone: "bg-stone-100 text-stone-700",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    blue: "bg-blue-100 text-blue-800",
    red: "bg-red-100 text-red-800",
};

function SummaryChip({ label, value, tone = "stone" }) {
    return (
        <div className={`rounded-lg px-3 py-2 ${TONE[tone] || TONE.stone}`}>
            <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
            <div className="font-display text-lg tabular-nums leading-none mt-1">{value ?? 0}</div>
        </div>
    );
}

function ConfidenceBadge({ score }) {
    const label = score >= 4 ? "High" : score >= 3 ? "Good" : score >= 2 ? "Low" : "Very low";
    const tone = score >= 4 ? "emerald" : score >= 3 ? "blue" : score >= 2 ? "amber" : "red";
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE[tone]}`} data-testid="confidence-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
            {label} confidence
        </span>
    );
}

function CapturesList({ rows, cards, onChange, readonly = false }) {
    if (!rows.length) {
        return (
            <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center text-sm text-stone-500" data-testid="captures-empty">
                <Sparkles size={20} className="mx-auto text-stone-400 mb-3" />
                {readonly
                    ? "No auto-captured transactions yet. Hit \"Scan last 30 days\" above to check your inbox."
                    : "Nothing waiting for your review right now — great job."}
            </div>
        );
    }
    return (
        <div className="space-y-3">
            {rows.map((r) => (
                <CaptureRow key={r.id} row={r} cards={cards} onChange={onChange} readonly={readonly} />
            ))}
        </div>
    );
}

function CaptureRow({ row, cards, onChange, readonly }) {
    const [merchant, setMerchant] = useState(row.merchant || "");
    const [amount, setAmount] = useState(String(row.amount ?? ""));
    const [category, setCategory] = useState(row.category || "Other");
    const [cardId, setCardId] = useState(row.card_id || "");
    const [bp, setBp] = useState("Personal");
    const [busy, setBusy] = useState(false);

    const cardOptions = cards.map((c) => ({
        value: c.id,
        label: `${c.bank_name} · ${c.card_name} · ••••${c.last_4}`,
    }));

    const accept = async () => {
        setBusy(true);
        try {
            await api.post(`/email-capture/captures/${row.id}/accept`, {
                card_id: cardId || row.card_id,
                merchant, category, amount: Number(amount || 0),
                business_personal: bp,
                remember_rule: category !== row.category,
            });
            toast.success("Posted");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    const reject = async () => {
        if (!window.confirm("Reject this transaction? It won't be posted as an expense.")) return;
        setBusy(true);
        try {
            await api.post(`/email-capture/captures/${row.id}/reject`);
            toast.success("Rejected");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border border-stone-200 bg-white rounded-xl p-4" data-testid={`capture-row-${row.id}`}>
            <div className="flex items-start gap-3 mb-3 flex-wrap">
                <div className="w-9 h-9 rounded-lg bg-stone-900 text-white flex items-center justify-center shrink-0">
                    <Sparkles size={14} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">
                        {row.bank_name || "Bank"} · ••••{row.last_4 || "----"}
                        <span className="text-stone-500 font-normal"> · {row.subject?.slice(0, 80) || "no subject"}</span>
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                        From {row.sender || "—"} · {row.received_date}
                    </div>
                </div>
                <ConfidenceBadge score={row.confidence || 0} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                    <label className="eyebrow text-[10px]">Merchant</label>
                    <Input value={merchant} onChange={(e) => setMerchant(e.target.value)} disabled={readonly || busy} className="mt-1" data-testid={`cap-merchant-${row.id}`} />
                </div>
                <div>
                    <label className="eyebrow text-[10px]">Amount</label>
                    <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={readonly || busy} className="mt-1" data-testid={`cap-amount-${row.id}`} />
                </div>
                <div>
                    <label className="eyebrow text-[10px]">Category</label>
                    <Select value={category} onValueChange={setCategory} disabled={readonly || busy}>
                        <SelectTrigger className="mt-1" data-testid={`cap-category-${row.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64">
                            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="eyebrow text-[10px]">Card</label>
                    <Select value={cardId} onValueChange={setCardId} disabled={readonly || busy}>
                        <SelectTrigger className="mt-1" data-testid={`cap-card-${row.id}`}>
                            <SelectValue placeholder="Pick a card" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                            {cardOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {!readonly && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-stone-600 flex items-center gap-2">
                        <input type="radio" checked={bp === "Personal"} onChange={() => setBp("Personal")} /> Personal
                    </label>
                    <label className="text-xs text-stone-600 flex items-center gap-2">
                        <input type="radio" checked={bp === "Business"} onChange={() => setBp("Business")} /> Business
                    </label>
                    <div className="ml-auto flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={reject} disabled={busy} data-testid={`cap-reject-${row.id}`}>
                            <X size={13} className="mr-1.5" /> Reject
                        </Button>
                        <Button size="sm" onClick={accept} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`cap-accept-${row.id}`}>
                            {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={13} className="mr-1.5" />}
                            Accept & post
                        </Button>
                    </div>
                </div>
            )}
            {readonly && row.expense_id && (
                <div className="mt-3 text-[11px] text-emerald-700 inline-flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> Posted as expense · {fmtMoney(row.amount || 0)}
                </div>
            )}
        </div>
    );
}

function RulesTab({ rules, onChange }) {
    const [pattern, setPattern] = useState("");
    const [category, setCategory] = useState("Other");
    const [refreshing, setRefreshing] = useState(false);
    const learned = rules.filter((r) => r.source === "learned");
    const defaults = rules.filter((r) => r.source === "default");

    const add = async () => {
        if (!pattern.trim()) return;
        try {
            await api.post("/email-capture/rules", { pattern, category });
            setPattern("");
            toast.success("Rule added");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this rule?")) return;
        try {
            await api.delete(`/email-capture/rules/${id}`);
            toast.success("Deleted");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const refreshDefaults = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post("/email-capture/rules/refresh-defaults");
            const added = data.inserted || 0;
            const updated = data.updated || 0;
            if (added === 0 && updated === 0) {
                toast.info("You already have all the latest default rules.");
            } else {
                toast.success(`${added} added · ${updated} updated · ${data.total_defaults} defaults total.`);
            }
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="rules-add">
                <div className="eyebrow mb-3">Teach the system a new rule</div>
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,220px)_auto] gap-3 items-end">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-stone-500">
                            Merchant pattern (regex or plain text — matched case-insensitively)
                        </label>
                        <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="e.g. porter|shifting" className="mt-1" data-testid="rules-pattern" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-stone-500">Category</label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger className="mt-1" data-testid="rules-category"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-64">
                                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={add} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="rules-add-btn">
                        + Add rule
                    </Button>
                </div>
                <p className="text-[11px] text-stone-500 mt-3 flex items-center gap-1.5">
                    <Info size={11} /> Learned rules take priority over defaults. They&rsquo;re also created automatically when you correct a category in the Needs Review queue.
                </p>
            </div>

            <RuleTable title={`Your learned rules · ${learned.length}`} rows={learned} onDelete={remove} deletable />
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm font-medium text-stone-800">
                        Default rules · {defaults.length}
                        <span className="text-xs text-stone-500 ml-2 font-normal">— ships with VAULT, auto-updated on refresh</span>
                    </div>
                    <button
                        type="button"
                        onClick={refreshDefaults}
                        disabled={refreshing}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-600 hover:text-stone-900 border border-stone-200 hover:border-stone-900 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                        data-testid="rules-refresh-defaults-btn"
                    >
                        {refreshing ? "Refreshing…" : "Refresh defaults"}
                    </button>
                </div>
                <RuleTableBody rows={defaults} />
            </div>
        </div>
    );
}

function RuleTableBody({ rows }) {
    if (rows.length === 0) {
        return <div className="p-6 text-sm text-stone-500 text-center">No rules yet.</div>;
    }
    return (
        <table className="dense-table w-full">
            <thead><tr><th>Pattern</th><th>Category</th></tr></thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.id}>
                        <td className="font-mono-figure text-xs">{r.pattern}</td>
                        <td>{r.category}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function RuleTable({ title, rows, onDelete, deletable = false }) {
    return (
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-100 text-sm font-medium text-stone-800">{title}</div>
            {rows.length === 0 ? (
                <div className="p-6 text-sm text-stone-500 text-center">No rules yet.</div>
            ) : (
                <table className="dense-table w-full">
                    <thead><tr><th>Pattern</th><th>Category</th><th className="text-right">{deletable && "Action"}</th></tr></thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td className="font-mono-figure text-xs">{r.pattern}</td>
                                <td>{r.category}</td>
                                <td className="text-right">
                                    {deletable && (
                                        <button
                                            onClick={() => onDelete(r.id)}
                                            className="text-red-700 hover:text-red-900 text-xs inline-flex items-center gap-1"
                                            data-testid={`rule-delete-${r.id}`}
                                        >
                                            <Trash2 size={11} /> Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function LogsTab({ logs }) {
    if (!logs.length) {
        return (
            <div className="rounded-2xl border border-stone-200 bg-white p-10 text-sm text-center text-stone-500">
                Parse logs will appear here once you sync your inbox.
            </div>
        );
    }
    return (
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
            <table className="dense-table w-full">
                <thead>
                    <tr>
                        <th>When</th><th>From</th><th>Subject</th>
                        <th className="text-right">Amount</th><th>Last 4</th><th>Merchant</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((l) => (
                        <tr key={l.id} data-testid={`log-${l.id}`}>
                            <td className="text-xs tabular-nums whitespace-nowrap">{fmtDateTime(l.parsed_at)}</td>
                            <td className="text-xs truncate max-w-[220px]">{l.sender}</td>
                            <td className="text-xs truncate max-w-[260px]">{l.subject}</td>
                            <td className="text-right tabular-nums">{l.parsed?.amount ? fmtMoney(l.parsed.amount) : <span className="text-red-700 text-xs">—</span>}</td>
                            <td className="font-mono-figure text-xs">{l.parsed?.last_4 ? `••••${l.parsed.last_4}` : <span className="text-red-700 text-xs">—</span>}</td>
                            <td className="text-xs">{l.parsed?.merchant || <span className="text-red-700">—</span>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// End of file
